import React, { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";

const PhotoPreview = ({ file, onRemove }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const preview = URL.createObjectURL(file);
    setUrl(preview);
    return () => URL.revokeObjectURL(preview);
  }, [file]);
  return (
    <div className="create-story-photo-item">
      {url && <img src={url} alt="Story photo preview" className="create-story-photo" />}
      <button type="button" className="create-story-photo-delete" aria-label="Remove photo" onClick={onRemove}>✕</button>
    </div>
  );
};

export default function StoryPhotoUpload({ photos, onChange }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [error, setError] = useState("");
  const [cropping, setCropping] = useState(false);

  useEffect(() => () => {
    if (selectedImage) URL.revokeObjectURL(selectedImage);
  }, [selectedImage]);

  const selectPhoto = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Please choose a JPG, PNG, or WebP image.");
      return;
    }
    setError("");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setArea(null);
    setSelectedImage(URL.createObjectURL(file));
  };

  const applyCrop = async () => {
    if (!area || cropping) return;
    setCropping(true);
    setError("");
    try {
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = selectedImage;
      });
      const canvas = document.createElement("canvas");
      canvas.width = 638;
      canvas.height = 380;
      canvas.getContext("2d").drawImage(image, area.x, area.y, area.width, area.height, 0, 0, 638, 380);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.95));
      if (!blob) throw new Error("Unable to crop image");
      onChange([new File([blob], "story-image.jpg", { type: "image/jpeg" })]);
      setSelectedImage(null);
    } catch {
      setError("Unable to crop this image. Please try another photo.");
    } finally {
      setCropping(false);
    }
  };

  return (
    <div className="create-story-group">
      <label>{t("uploadphotos")}</label>
      <div
        className="create-story-drop-zone"
        role="button"
        tabIndex={0}
        aria-label="Upload photos"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          selectPhoto(event.dataTransfer.files[0]);
        }}
      >
        <span className="create-story-upload-icon">📷</span>
        <p>{t("draganddrop")}</p>
        <input ref={inputRef} type="file" aria-label="Choose story photo" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
          onChange={(event) => {
            selectPhoto(event.target.files[0]);
            event.target.value = "";
          }}
        />
      </div>
      <div className="create-story-photo-preview">
        {photos.map((file, index) => (
          <PhotoPreview key={index} file={file} onRemove={() => onChange(photos.filter((_, i) => i !== index))} />
        ))}
      </div>
      {error && !selectedImage && <p role="alert">{error}</p>}
      {selectedImage && (
        <div className="crop-overlay" role="dialog" aria-modal="true" aria-label="Crop Image">
          <div className="crop-modal">
            <div className="crop-header">
              <h3>Crop Image</h3>
              <button type="button" aria-label="Close crop" disabled={cropping} onClick={() => setSelectedImage(null)}>✕</button>
            </div>
            <div className="cropper-container">
              <Cropper image={selectedImage} crop={crop} zoom={zoom} aspect={638 / 380} objectFit="cover"
                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, pixels) => setArea(pixels)} />
            </div>
            <div className="crop-controls">
              <input type="range" aria-label="Photo zoom" min={1} max={3} step={0.1} value={zoom} onChange={(event) => setZoom(Number(event.target.value))} />
              {error && <p role="alert">{error}</p>}
              <div className="crop-action-buttons">
                <button type="button" className="cancel-btn" disabled={cropping} onClick={() => setSelectedImage(null)}>Cancel</button>
                <button type="button" className="apply-btn" disabled={!area || cropping} onClick={applyCrop}>{cropping ? "Cropping..." : "Apply Crop"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
